import { POST_ACCOUNT_SUCCESS } from 'stash/actions/api';
import { BOOTSTRAP_GET_ACCOUNTS_SUCCESS } from 'stash/store/bootstrapActions';
import { SET_MULTI_AUTOSTASH, SET_STASH_BANK } from 'stash/actions';

type AccountType = 'PERSONAL_BROKERAGE' | 'TRADITIONAL_IRA' | 'ROTH_IRA' | 'BANK';

type AccountMapping = {
	typeEnum: string;
	amount: number;
	frequency: string;
	order: number;
};

type AccountRecord = {
	id: string | null;
	type: AccountType;
	title: string;
};

export type AccountToFund = {
	id: string | null;
	type: AccountType;
	typeEnum: string;
	title: string;
	amount: number;
	frequency: string;
	order: number;
};

type AccountsToFund = Record<string, AccountToFund>;

type AutoStashData = Record<string, { amount: number; frequency: string }>;

type MultiFundingState = {
	accountsToFund: AccountsToFund;
	order: string[];
};

type MultiFundingAction = {
	type: string;
	data?: AutoStashData | Partial<AccountToFund>;
	response?: {
		account?: AccountRecord;
		accounts?: Record<string, AccountRecord>;
	};
};

export const accountsMap: Record<AccountType, AccountMapping> = {
	PERSONAL_BROKERAGE: {
		typeEnum: 'personalBrokerage',
		amount: 50,
		frequency: 'weekly',
		order: 0,
	},
	TRADITIONAL_IRA: {
		typeEnum: 'traditionalIra',
		amount: 50,
		frequency: 'weekly',
		order: 1,
	},
	ROTH_IRA: {
		typeEnum: 'rothIra',
		amount: 50,
		frequency: 'weekly',
		order: 2,
	},
	BANK: {
		typeEnum: 'stashBank',
		amount: 30,
		frequency: 'weekly',
		order: 3,
	},
};

export const bankAccount: AccountToFund = {
	id: null,
	type: 'BANK',
	typeEnum: accountsMap['BANK'].typeEnum,
	title: 'Stash banking',
	amount: accountsMap['BANK'].amount,
	frequency: accountsMap['BANK'].frequency,
	order: accountsMap['BANK'].order,
};

export const getAccountToFund = (account: AccountRecord): AccountToFund => {
	const accountMapping = accountsMap[account.type];

	return {
		id: account.id,
		type: account.type,
		typeEnum: accountMapping.typeEnum,
		title: account.title,
		amount: accountMapping.amount,
		frequency: accountMapping.frequency,
		order: accountMapping.order,
	};
};

export const getAccountsToFund = (
	accounts: Record<string, AccountRecord> | AccountRecord[]
): AccountsToFund => {
	const accountsValue = Object.values(accounts);

	const hasInvestAccount =
		accountsValue.findIndex((account) => {
			return account.type === 'PERSONAL_BROKERAGE';
		}) > -1;

	const initialAccounts: AccountsToFund = hasInvestAccount
		? {
				stashBank: bankAccount,
			}
		: {};

	return accountsValue.length
		? accountsValue.reduce<AccountsToFund>((acc, account) => {
				const accountMapping = accountsMap[account.type];

				return accountMapping && accountMapping.typeEnum
					? {
							...acc,
							[accountMapping.typeEnum]: getAccountToFund(account),
						}
					: acc;
			}, initialAccounts)
		: {};
};

export const updateAccountsToFund = (
	accountsToFund: AccountsToFund,
	accountData: AutoStashData
): AccountsToFund => {
	const accountsValue = Object.values(accountsToFund);

	return accountsValue.reduce<AccountsToFund>((acc, account) => {
		const accountMapping = accountsMap[account.type];

		return accountMapping
			? {
					...acc,
					[accountMapping.typeEnum]: {
						...accountsToFund[accountMapping.typeEnum],
						amount: accountData[`${accountMapping.typeEnum}`].amount,
						frequency: accountData[`${accountMapping.typeEnum}`].frequency,
					},
				}
			: acc;
	}, {});
};

export const getSortedAccounts = (accountsToFund: AccountsToFund): string[] => {
	return Object.values(accountsToFund)
		.sort((a, b) => {
			return a.order - b.order;
		})
		.map((account) => account.typeEnum);
};

export const initState: MultiFundingState = {
	accountsToFund: {},
	order: [],
};

export const multiFunding = (
	state: MultiFundingState = initState,
	action: MultiFundingAction
): MultiFundingState => {
	let accountsToFund: AccountsToFund;
	switch (action.type) {
		case SET_MULTI_AUTOSTASH:
			return {
				...state,
				accountsToFund: updateAccountsToFund(state.accountsToFund, action.data as AutoStashData),
			};

		case SET_STASH_BANK:
			return {
				...state,
				accountsToFund: {
					...state.accountsToFund,
					stashBank: {
						...state.accountsToFund.stashBank,
						...(action.data as Partial<AccountToFund>),
					},
				},
			};

		case POST_ACCOUNT_SUCCESS:
			accountsToFund = {
				...state.accountsToFund,
				...(action.response?.account ? getAccountsToFund([action.response.account]) : {}),
			};

			return {
				...state,
				accountsToFund: {
					...state.accountsToFund,
					...accountsToFund,
				},
				order: getSortedAccounts(accountsToFund),
			};

		case BOOTSTRAP_GET_ACCOUNTS_SUCCESS:
			accountsToFund = getAccountsToFund(action.response?.accounts ?? {});

			return {
				...state,
				accountsToFund,
				order: getSortedAccounts(accountsToFund),
			};

		default:
			return state;
	}
};

export default multiFunding;
